import {
  Activity,
  Building2,
  Gauge,
  History,
  LifeBuoy,
  LogOut,
  Settings,
  Shield,
  UserCircle,
  Users,
  ArchiveRestore,
  type LucideIcon,
} from 'lucide-react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { buildPlatformNavigation, type PlatformNavigationId } from '@/security/platformNavigation'
import { getRoleLabel } from '@/security/accessContract'
import SupportAccessBanner from '@/components/SupportAccessBanner'

const icons = {
  'business-review': Shield,
  'subject-review': Shield,
  'source-review': Shield,
  policy: Shield,
  overview: Gauge,
  tenants: Building2,
  users: Users,
  monitoring: Activity,
  operations: History,
  support: LifeBuoy,
  audit: Shield,
  deletions: ArchiveRestore,
  profile: UserCircle,
  settings: Settings,
} satisfies Record<PlatformNavigationId, LucideIcon>

export default function PlatformAppLayout() {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  if (!user) return null
  const items = buildPlatformNavigation(user)

  return (
    <div className="flex min-h-screen bg-surface-light dark:bg-surface-dark">
      <aside className="w-64 border-r border-surface-overlay p-4 flex flex-col">
        <button className="text-left mb-8" onClick={() => navigate(items[0]?.path ?? '/403')}>
          <span className="block text-lg font-bold text-text-primary">TaxHive Platform</span>
          <span className="text-xs text-text-tertiary">CML control plane</span>
        </button>
        <nav className="space-y-1 flex-1" aria-label="Platform navigation">
          {items.map(item => {
            const Icon = icons[item.id]
            const active =
              item.path === '/platform'
                ? location.pathname === item.path
                : location.pathname.startsWith(item.path)
            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                aria-current={active ? 'page' : undefined}
                className={`w-full flex items-center gap-3 rounded-lg px-3 py-2 text-left ${active ? 'bg-primary-100 text-primary-700' : 'text-text-secondary hover:bg-surface-overlay'}`}
              >
                <Icon className="w-4 h-4" />
                <span>{item.label}</span>
              </button>
            )
          })}
        </nav>
        <div className="border-t border-surface-overlay pt-4 text-sm">
          <div className="font-medium text-text-primary truncate">
            {user.fullName || user.email}
          </div>
          <div className="text-text-tertiary text-xs mb-3">{getRoleLabel(user.role)}</div>
          <button
            className="flex items-center gap-2 text-text-secondary"
            onClick={() => void logout()}
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>
      <div className="min-w-0 flex-1">
        <header className="flex items-center justify-end gap-2 border-b border-surface-overlay px-4 py-3 sm:px-8">
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/platform/profile')}>
            <UserCircle className="h-4 w-4" /> Profile
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/platform/settings')}>
            <Settings className="h-4 w-4" /> Settings
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => void logout()}>
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </header>
        <main className="overflow-auto p-4 sm:p-8">
          <SupportAccessBanner />
          <Outlet />
        </main>
      </div>
    </div>
  )
}
